/**
 * The flavour finder's enhancement layer.
 *
 * The filter itself is not here. Two native radio groups and a set of generated `:has()` rules do
 * the whole job, so the journey works with this file absent: tapping a family filters the deck,
 * tapping a lane narrows it to that rung of the puff ladder, and the reset button clears both. What
 * this adds is the four things CSS cannot do.
 *
 * 1. Shareable state. The two answers go into the query string on every change, so a result can be
 *    linked, bookmarked and reloaded, and so a campaign can point at `?flavour=candy-cola-energy`.
 * 2. The hero's deep link. Each slide's "Discover it" points at `#pick-<family>`, which is the id of
 *    that family's label. Without this file the browser scrolls to the control and the reader taps
 *    it; with it, the answer is already given and only the second question is left. `hashchange` is
 *    listened for too, because clicking a slide while already on the page never reloads.
 * 3. The announcement. A CSS filter changes what is visible, and visibility is not a DOM mutation,
 *    so no live region fires on its own. Each verdict carries the exact sentence to speak on
 *    `data-wtf-say`, authored where the counts are, and this copies it into the status line rather
 *    than scraping the rendered text: the visible verdict prints a slash between the two answers,
 *    which is punctuation for the eye and noise for a screen reader.
 */

const HASH_PREFIX = '#pick-';
const SAFE_VALUE = /^[a-z0-9-]+$/;

class NastyWtfFinder extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('[data-wtf-form]');
    if (!this.form) return;

    this.out = this.querySelector('[data-wtf-out]');
    this.deck = this.querySelector('[data-wtf-deck]');
    this.statusLine = this.querySelector('[data-wtf-status]');
    this.quickFlavour = this.querySelector('[data-wtf-quick-flavour]');
    this.quickResult = this.querySelector('[data-wtf-quick-result]');
    this.quickClear = this.querySelector('[data-wtf-quick-clear]');
    this.lastShown = null;
    this.quickDevice = this.querySelector('[data-wtf-quick-device]');
    this.quickBar = this.querySelector('.nasty-g-wtf-finder__result-bar');
    this.matchCta = this.querySelector('[data-wtf-match-cta]');
    this.wasAnswered = false;
    this.stickyFrame = 0;
    this.revealFrame = 0;
    this.revealTimer = 0;

    this.onChange = this.onChange.bind(this);
    this.onReset = this.onReset.bind(this);
    this.onHash = this.onHash.bind(this);
    this.onPickLink = this.onPickLink.bind(this);
    this.onViewportChange = this.onViewportChange.bind(this);
    this.onSubmit = (event) => event.preventDefault();

    this.form.addEventListener('change', this.onChange);
    this.form.addEventListener('reset', this.onReset);
    this.form.addEventListener('submit', this.onSubmit);
    this.quickFlavour?.addEventListener('change', () => this.onQuickChange('flavour', this.quickFlavour.value));
    this.quickDevice?.addEventListener('change', () => this.onQuickChange('device', this.quickDevice.value));
    /*
     * `form.reset()` fires the form's own reset event, so the existing handler re-syncs everything.
     * Then back up to "Find your flavour": clearing means starting the picks again, and the picks
     * live in the black band above, not in the deck the reader is looking at.
     */
    this.quickClear?.addEventListener('click', () => {
      this.form.reset();
      this.reveal(this.form, true);
    });
    /* The panel's Start over is a native reset bound by `form`; the glide is the only extra. */
    this.querySelector('[data-wtf-start-over]')?.addEventListener('click', () => {
      this.reveal(this.form, true);
    });
    window.addEventListener('hashchange', this.onHash);
    this.scrollRoot = this.scroller();
    this.scrollRoot?.addEventListener('scroll', this.onViewportChange, { passive: true });
    window.addEventListener('resize', this.onViewportChange, { passive: true });

    /*
     * The hero's slides are handled here rather than left to the browser, and that is a fix rather
     * than a preference. On a phone the document is the scroller and a `#pick-` link behaves: it
     * lands on the control and the answer is given. On a desktop Horizon scrolls `.page-wrapper`,
     * and a fragment navigation into that inner scroller moves nothing at all: the radio was
     * checked, the page stayed on the banner, and the step that had just been answered was 1,200px
     * below the fold. Which read, correctly, as the banner not working.
     *
     * Taking the click also fixes the second press. `hashchange` does not fire when the hash is
     * already what it is about to be set to, so tapping the same slide's button twice did nothing
     * the second time.
     */
    document.addEventListener('click', this.onPickLink);

    this.restore();
    this.syncQuickResult();
    this.updateStickyBar();
  }

  disconnectedCallback() {
    window.removeEventListener('hashchange', this.onHash);
    document.removeEventListener('click', this.onPickLink);
    this.scrollRoot?.removeEventListener('scroll', this.onViewportChange);
    window.removeEventListener('resize', this.onViewportChange);
    if (this.stickyFrame) cancelAnimationFrame(this.stickyFrame);
    if (this.revealFrame) cancelAnimationFrame(this.revealFrame);
    if (this.revealTimer) window.clearTimeout(this.revealTimer);
  }

  onViewportChange() {
    if (this.stickyFrame) return;
    this.stickyFrame = requestAnimationFrame(() => {
      this.stickyFrame = 0;
      this.updateStickyBar();
    });
  }

  updateStickyBar() {
    if (!this.quickBar || !this.form) return;
    const top = parseFloat(getComputedStyle(this.quickBar).top) || 0;
    /*
     * The bar arrives as the results do: when the first device group's rule reaches the bar's
     * lower edge, so it slides in over the deck it filters rather than over the picker or the
     * gap after it. The deck is `display: none` when a pick has nothing to show, which gives it no
     * box, so the results block it sits in stands in for it then.
     */
    const deck = this.deck?.getClientRects().length ? this.deck : this.out;
    const anchor = deck || this.form;
    const edge = top + this.quickBar.offsetHeight;
    const formHasPassed = deck
      ? anchor.getBoundingClientRect().top <= edge
      : anchor.getBoundingClientRect().bottom <= top;
    const finderStillVisible = this.getBoundingClientRect().bottom > top + this.quickBar.offsetHeight;
    const active = formHasPassed && finderStillVisible;
    this.quickBar.toggleAttribute('data-sticky-active', active);
    this.quickBar.setAttribute('aria-hidden', active ? 'false' : 'true');
  }

  /* ----- state ----- */

  answer(name) {
    const group = this.form.elements[name];
    return group ? group.value : '';
  }

  get answered() {
    return this.answer('flavour') !== '' && this.answer('device') !== '';
  }

  /*
   * Values arrive from a URL, so they are checked against the markup rather than trusted: a handle
   * that names no control is ignored, and anything that is not a handle never reaches a selector.
   */
  choose(name, value) {
    if (!value || !SAFE_VALUE.test(value)) return false;
    const input = this.querySelector(`[data-wtf-${name}="${value}"]`);
    if (!input || input.checked) return Boolean(input);
    input.checked = true;
    return true;
  }

  restore() {
    const params = new URLSearchParams(window.location.search);
    let found = this.choose('flavour', params.get('flavour'));
    found = this.choose('device', params.get('device')) || found;
    const chosenDevice = this.form.querySelector('[data-wtf-device]:checked');
    const deviceLane = chosenDevice?.nextElementSibling?.dataset.lane;
    found = this.choose('tier', deviceLane || params.get('lane')) || found;
    this.reconcileFamily();

    /* The hash is the newer intent: it is a slide the reader just tapped. */
    const fromHash = window.location.hash.startsWith(HASH_PREFIX);
    if (fromHash) {
      found = this.choose('flavour', window.location.hash.slice(HASH_PREFIX.length)) || found;
    }

    if (found) this.sync(false);

    /*
     * A link into `#pick-` means take me to that step, so the load honours it. A shared
     * `?flavour=&lane=` link does not scroll: it is a result someone sent, and the page should open
     * where the reader put it. A turn of the event loop rather than a frame, because the figures
     * above are still being laid out and because `requestAnimationFrame` never runs in a background
     * tab, which would leave a bookmarked link landing in the wrong place.
     */
    if (fromHash) window.setTimeout(() => this.reveal(this.form), 0);
  }

  onHash() {
    if (!window.location.hash.startsWith(HASH_PREFIX)) return;
    if (this.choose('flavour', window.location.hash.slice(HASH_PREFIX.length))) {
      this.sync(false);
      this.reveal(this.form);
    }
  }

  /*
   * Any `#pick-` link on the page, wherever it lives. `preventDefault` because the browser's own
   * fragment scroll is the thing that does not work here, and because the query string this writes
   * is the state worth having in the URL; the id of a control is not.
   */
  onPickLink(event) {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey) return;
    const link = event.target.closest('a[href*="#pick-"]');
    if (!link) return;

    const family = link.getAttribute('href').split('#pick-')[1];
    if (!this.choose('flavour', family)) return;

    event.preventDefault();
    this.sync(false);
    this.reveal(this.form);
  }

  onChange(event) {
    if (event.target.matches('[data-wtf-tier]')) {
      const chosenDevice = this.form.querySelector('[data-wtf-device]:checked');
      if (chosenDevice && chosenDevice.nextElementSibling?.dataset.lane !== event.target.value) {
        chosenDevice.checked = false;
      }
    }
    if (event.target.matches('[data-wtf-flavour]')) {
      this.reconcileFamily();
    }
    /*
     * Picking a power is the last step, so every pick hands the reader to the results, not just the
     * first one: changing your mind about the device should land you on the new answer too.
     */
    const pickedDevice = event.target.matches('[data-wtf-device]');
    this.sync(this.answered && (pickedDevice || !this.wasAnswered));
  }

  onQuickChange(name, value) {
    const group = this.form.querySelectorAll(`[name="${name}"]`);
    group.forEach((input) => {
      input.checked = input.value === value;
    });
    if (name === 'flavour') this.reconcileFamily();
    this.sync(false);
  }

  reconcileFamily() {
    const family = this.answer('flavour');
    if (!family) return;
    const supports = (option) => option?.dataset.families?.split(/\s+/).includes(family);
    const chosenDevice = this.form.querySelector('[data-wtf-device]:checked');
    if (chosenDevice && !supports(chosenDevice.nextElementSibling)) chosenDevice.checked = false;
    const chosenTier = this.form.querySelector('[data-wtf-tier]:checked');
    if (!chosenTier) return;
    const available = [...this.form.querySelectorAll('.nasty-g-wtf-finder__devices [data-lane]')]
      .some((option) => option.dataset.lane === chosenTier.value && supports(option));
    if (!available) chosenTier.checked = false;
  }

  /* `reset` fires before the controls are cleared, so the read waits a turn. */
  onReset() {
    window.setTimeout(() => {
      this.sync(false);
    }, 0);
  }

  sync(scroll) {
    this.wasAnswered = this.answered;
    if (this.matchCta) this.matchCta.hidden = !this.answered;
    this.syncQuickFilters();
    this.writeUrl();
    this.say();
    if (scroll) this.reveal(this.out, true);
  }

  syncQuickFilters() {
    const flavour = this.answer('flavour');
    const device = this.answer('device');
    if (this.quickFlavour) this.quickFlavour.value = flavour;

    if (this.quickDevice) {
      Array.from(this.quickDevice.options).forEach((option) => {
        if (!option.value) return;
        const families = (option.dataset.families || '').split(/\s+/);
        option.disabled = Boolean(flavour) && !families.includes(flavour);
      });
      this.quickDevice.value = device;
    }

    this.syncQuickResult();
  }

  /*
   * Counted from the cards that are showing rather than looked up, because the CSS filter is what
   * decides what shows and this should never be able to disagree with it. `getClientRects` is empty
   * for anything under a `display: none` ancestor, which is how a hidden group or deck hides its
   * cards, and reading it forces the style the change just applied.
   */
  syncQuickResult() {
    if (!this.quickResult) return;
    const shown = [...this.querySelectorAll('[data-wtf-hit]')].filter((hit) => hit.getClientRects().length > 0).length;
    const filtered = Boolean(this.answer('flavour') || this.answer('device'));
    if (this.quickClear) this.quickClear.hidden = !filtered;

    if (shown === 0) {
      this.quickResult.textContent = 'No matching flavours';
      this.lastShown = shown;
      return;
    }

    const noun = shown === 1 ? 'flavour' : 'flavours';
    const number = document.createElement('span');
    number.className = 'nasty-g-wtf-finder__quick-number';
    number.textContent = String(shown);
    if (this.lastShown !== null && this.lastShown !== shown) number.toggleAttribute('data-ticked', true);
    const label = document.createElement('span');
    label.textContent = filtered ? `matching ${noun}` : noun;
    this.quickResult.replaceChildren(number, label);
    this.lastShown = shown;
  }


  writeUrl() {
    const url = new URL(window.location.href);
    const flavour = this.answer('flavour');
    const device = this.answer('device');
    const lane = this.answer('power_lane');

    if (flavour) url.searchParams.set('flavour', flavour);
    else url.searchParams.delete('flavour');
    if (device) url.searchParams.set('device', device);
    else url.searchParams.delete('device');
    if (lane) url.searchParams.set('lane', lane);
    else url.searchParams.delete('lane');

    window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
  }

  /* ----- announcement ----- */

  verdict() {
    if (!this.answered) return null;
    const cell = `${this.answer('flavour')}/${this.answer('device')}`;
    return this.querySelector(`[data-wtf-verdict][data-cell="${cell}"]`);
  }

  say(extra) {
    if (!this.statusLine) return;
    const verdict = this.verdict();
    const said = verdict ? verdict.getAttribute('data-wtf-say') || '' : '';
    this.statusLine.textContent = [said, extra].filter(Boolean).join(' ');
  }

  /* ----- motion ----- */

  /*
   * The scroll container, found rather than assumed. On a phone it is the document; on a desktop
   * Horizon scrolls `.page-wrapper` and the window does not move at all.
   */
  scroller() {
    let node = this.parentElement;
    while (node && node !== document.body) {
      const style = getComputedStyle(node);
      if (/(auto|scroll)/.test(style.overflowY) && node.scrollHeight > node.clientHeight) return node;
      node = node.parentElement;
    }
    return document.scrollingElement;
  }

  /*
   * Horizon scrolls an inner `.page-wrapper`, where native smooth scrolling is cancelled. Use a
   * short ease-out tween only for the handoff from the second answer to the results. Deep links keep
   * their immediate anchor behaviour, reduced-motion users get an instant move, and the timeout
   * guarantees the final position even when a background tab pauses animation frames.
   */
  reveal(element, smooth = false) {
    if (!element) return;
    const scroller = this.scroller();
    const target = () => {
      const margin = parseFloat(getComputedStyle(element).scrollMarginTop) || 0;
      const max = scroller.scrollHeight - scroller.clientHeight;
      return Math.min(max, Math.max(0, scroller.scrollTop + element.getBoundingClientRect().top - margin));
    };
    if (Math.abs(target() - scroller.scrollTop) < 8) return;

    if (this.revealFrame) cancelAnimationFrame(this.revealFrame);
    if (this.revealTimer) window.clearTimeout(this.revealTimer);

    if (!smooth || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      scroller.scrollTo({ top: target(), behavior: 'instant' });
      return;
    }

    /*
     * Ease in and out over 0.7 to 1.1s depending on distance, so the move reads as a glide rather
     * than a jump. The destination is re-measured every frame: the deck and its images change height
     * as the answer lands, and a target fixed at the first frame would finish short and then snap.
     */
    const start = scroller.scrollTop;
    const duration = Math.min(1100, Math.max(700, Math.abs(target() - start) * 0.6));
    const started = performance.now();
    const finish = () => {
      if (this.revealFrame) cancelAnimationFrame(this.revealFrame);
      if (this.revealTimer) window.clearTimeout(this.revealTimer);
      scroller.scrollTo({ top: target(), behavior: 'instant' });
      this.revealFrame = 0;
      this.revealTimer = 0;
    };
    const step = (now) => {
      const progress = Math.min((now - started) / duration, 1);
      const eased = progress < 0.5 ? 4 * progress ** 3 : 1 - Math.pow(-2 * progress + 2, 3) / 2;
      /*
       * `instant`, not a `scrollTop` write: `.page-wrapper` carries `scroll-behavior: smooth`, so a
       * plain write starts the browser's own smooth scroll, which the next frame cancels. The page
       * barely moved during the tween and then jumped on the last frame.
       */
      scroller.scrollTo({ top: start + (target() - start) * eased, behavior: 'instant' });
      if (progress < 1) this.revealFrame = requestAnimationFrame(step);
      else finish();
    };

    this.revealFrame = requestAnimationFrame(step);
    this.revealTimer = window.setTimeout(finish, duration + 200);
  }
}

if (!customElements.get('nasty-wtf-finder')) {
  customElements.define('nasty-wtf-finder', NastyWtfFinder);
}
